/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React from 'react';
import { useNavigate } from 'react-router-dom';

type HtmlStringDisplayerProps = {
  html: string;
};

/**
 * Display an HTML string and handle click events on anchor elements using React Router.
 *
 * We want to use React Router for links because it allows us to open a link
 * without refreshing (i.e. reloading) the entire page.  This gives a smooth seamless experience.
 *
 * Note, use RenderedHTML instead if the html is contextualized.
 *
 * @param html - The HTML string to be displayed.
 */
export function HtmlStringDisplayer({ html }: HtmlStringDisplayerProps) {
  const navigate = useNavigate();

  /**
   * Handle click events on anchor elements.
   *
   * @param e - The mouse event.
   */
  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    // Check if the target is an <a> element and if the href attribute exists
    const target = e.target as HTMLElement; // Cast to HTMLElement to access the 'closest' method
    const link = target.closest?.('a[href]');

    if (link) {
      e.preventDefault(); // Prevent the default link behavior
      const href = link.getAttribute('href');
      if (href) {
        navigate(href); // Use navigate to change the route
      }
    }
  };

  return <div dangerouslySetInnerHTML={{ __html: html }} onClick={handleClick} />;
}
